import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from 'flow/types/Api';
import {
    UaePassError,
    UaePassLoginData,
    UaePassFinishResponse,
    UaePassTokensData,
    UaePassWindowCloseReason,
    UaePassFinishRequest,
} from 'flow/types/UaePassLogin';
import {
    fetchUaePassSignIn,
    fetchUaePassFinish,
} from 'flow/utils/uaepass-api-utils';
import { isAllowedUaePassUrl } from 'flow/utils/uae-pass-url';
import { getElectronAPI } from 'flow/utils/electron-api';
import isElectron from 'is-electron';
import { uaePassErrors } from 'flow/constants/uae-pass/uae-pass-constants';

type UseUAEPassOptions = {
    onWindowClosed?: (reason: UaePassWindowCloseReason) => void;
};

type UseUAEPassReturn = {
    signIn: () => void;
    signInData: UaePassLoginData | undefined;
    isSignInPending: boolean;
    signInError: ApiError | null;
    finishSignIn: (requestData: UaePassFinishRequest) => void;
    finishSignInData: UaePassFinishResponse | undefined;
    isFinishSignInPending: boolean;
    finishSignInError: ApiError | null;
    resetUaePass: () => void;
    uaePassError: UaePassError | null;
    clearUaePassError: () => void;
    closeUaePassWindow: () => void;
    handleUaePassTokens: (tokensData: UaePassTokensData) => void;
};

const openElectronUaePass = async (uaePassUrl: string) => {
    const electronAPI = getElectronAPI();
    if (!electronAPI) return;

    try {
        await electronAPI.uaePassLogin({ uaePassUrl });
    } catch (error) {
        console.error('Failed to complete UAE Pass login:', error);
    }
};

const closeElectronUaePass = () => {
    if (!isElectron()) return;
    const electronAPI = getElectronAPI();
    if (!electronAPI) return;

    electronAPI.uaePassClose();
};

export const useUAEPass = ({
    onWindowClosed,
}: UseUAEPassOptions = {}): UseUAEPassReturn => {
    const [uaePassError, setUaePassError] = useState<UaePassError | null>(null);

    const {
        mutate: signIn,
        data: signInData,
        isPending: isSignInPending,
        error: signInError,
        reset: resetSignIn,
    } = useMutation<UaePassLoginData, ApiError>({
        mutationFn: () => fetchUaePassSignIn(),
    });

    const {
        mutate: finishSignIn,
        data: finishSignInData,
        isPending: isFinishSignInPending,
        error: finishSignInError,
        reset: resetFinishSignIn,
    } = useMutation<UaePassFinishResponse, ApiError, UaePassFinishRequest>({
        mutationFn: (requestData: UaePassFinishRequest) =>
            fetchUaePassFinish(requestData),
    });

    useEffect(() => {
        const { uaePassUrl } = signInData || {};
        if (!uaePassUrl) return;

        if (isElectron()) {
            openElectronUaePass(uaePassUrl);
            return;
        }

        if (!isAllowedUaePassUrl(uaePassUrl)) {
            setUaePassError(uaePassErrors.SERVICE_NOT_FOUND);
            return;
        }
        window.location.assign(uaePassUrl);
    }, [signInData]);

    const handleUaePassTokens = useCallback(
        ({ sessionToken, error }: UaePassTokensData) => {
            if (error) {
                setUaePassError(error);
            } else if (sessionToken) {
                finishSignIn({ token: sessionToken });
            }
        },
        [finishSignIn],
    );

    useEffect(() => {
        if (!isElectron()) return undefined;
        const electronAPI = getElectronAPI();
        if (!electronAPI) return undefined;

        electronAPI.onUaePassTokens(handleUaePassTokens);

        return () => {
            electronAPI.removeUaePassTokensListener();
        };
    }, [finishSignIn, handleUaePassTokens]);

    useEffect(() => {
        if (!isElectron() || !onWindowClosed) return undefined;
        const electronAPI = getElectronAPI();
        if (!electronAPI) return undefined;

        electronAPI.onUaePassWindowClosed(onWindowClosed);

        return () => {
            electronAPI.removeUaePassWindowClosedListener();
        };
    }, [onWindowClosed]);

    useEffect(() => {
        if (!isElectron()) return undefined;
        return () => {
            closeElectronUaePass();
        };
    }, []);

    const clearUaePassError = useCallback(() => setUaePassError(null), []);

    const resetUaePass = useCallback(() => {
        resetSignIn();
        resetFinishSignIn();
    }, [resetSignIn, resetFinishSignIn]);

    return {
        signIn,
        signInData,
        isSignInPending,
        signInError,
        finishSignIn,
        finishSignInData,
        isFinishSignInPending,
        finishSignInError,
        resetUaePass,
        uaePassError,
        clearUaePassError,
        closeUaePassWindow: closeElectronUaePass,
        handleUaePassTokens,
    };
};
